import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../utils/axios.ts';

export interface PendingMember {
  id: number;
  name: string;
  phone_number: string;
  email?: string;
  id_no?: string;
  onboarding_token: string;
  token_expires_at: string;
  status: 'pending' | 'approved' | 'rejected';
  rejection_reason?: string;
  approved_by?: number;
  rejected_by?: number;
  approved_at?: string;
  rejected_at?: string;
  created_at: string;
  updated_at: string;
  additional_data?: any;
  approvedBy?: {
    id: number;
    name: string;
  };
  rejectedBy?: {
    id: number;
    name: string;
  };
}

export interface OnboardingLink {
  token: string;
  url: string;
  expires_at: string;
  expires_in_days: number;
}

export interface ExistingOnboardingLink {
  id: number;
  token: string;
  url: string;
  expires_at: string;
  usage_count: number;
  last_used_at: string | null;
  created_at: string;
  days_remaining: number;
}

export interface PendingMemberStats {
  total_pending: number;
  total_approved: number;
  total_rejected: number;
  expired_tokens: number;
  active_tokens: number;
}

export interface PendingMembersFilters {
  search?: string;
  status?: 'all' | 'pending' | 'approved' | 'rejected';
  page?: number;
  per_page?: number;
}

export interface PendingMembersResponse {
  data: PendingMember[];
  meta: {
    current_page: number;
    last_page: number;
    per_page: number;
    total: number;
  };
}

// Hook to get pending members
export const usePendingMembers = (filters: PendingMembersFilters = {}) => {
  return useQuery<PendingMembersResponse>({
    queryKey: ['pending-members', filters],
    queryFn: async () => {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params.append(key, value.toString());
      });
      
      const { data } = await api.get(`/pending-members?${params.toString()}`);
      return data;
    },
  });
};

// Hook to get pending member statistics
export const usePendingMemberStats = () => {
  return useQuery<PendingMemberStats>({
    queryKey: ['pending-member-stats'],
    queryFn: async () => {
      const { data } = await api.get('/pending-members/stats');
      return data.data;
    },
  });
};

// Hook to generate onboarding link
export const useGenerateOnboardingLink = () => {
  const queryClient = useQueryClient();
  
  return useMutation<OnboardingLink, Error, { expires_in_days?: number }>({
    mutationFn: async (data) => {
      const response = await api.post('/pending-members/generate-link', data);
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pending-member-stats'] });
      queryClient.invalidateQueries({ queryKey: ['onboarding-links'] });
    },
  });
};

// Hook to fetch existing onboarding links
export const useOnboardingLinks = () => {
  return useQuery<ExistingOnboardingLink[]>({
    queryKey: ['onboarding-links'],
    queryFn: async () => {
      const response = await api.get('/pending-members/links');
      return response.data.data;
    },
    staleTime: 30000, // 30 seconds
  });
};

// Hook to approve a pending member
export const useApprovePendingMember = () => {
  const queryClient = useQueryClient();
  
  return useMutation<any, Error, number>({
    mutationFn: async (id) => {
      const response = await api.post(`/pending-members/${id}/approve`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pending-members'] });
      queryClient.invalidateQueries({ queryKey: ['pending-member-stats'] });
      queryClient.invalidateQueries({ queryKey: ['members'] });
    },
  });
};

// Hook to reject a pending member
export const useRejectPendingMember = () => {
  const queryClient = useQueryClient();
  
  return useMutation<any, Error, { id: number; reason?: string }>({
    mutationFn: async ({ id, reason }) => {
      const response = await api.post(`/pending-members/${id}/reject`, { reason });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pending-members'] });
      queryClient.invalidateQueries({ queryKey: ['pending-member-stats'] });
    },
  });
};

// Hook to bulk approve pending members
export const useBulkApprovePendingMembers = () => {
  const queryClient = useQueryClient();
  
  return useMutation<any, Error, number[]>({
    mutationFn: async (ids) => {
      const response = await api.post('/pending-members/bulk-approve', { ids });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pending-members'] });
      queryClient.invalidateQueries({ queryKey: ['pending-member-stats'] });
      queryClient.invalidateQueries({ queryKey: ['members'] });
    },
  });
};

// Hook to bulk reject pending members
export const useBulkRejectPendingMembers = () => {
  const queryClient = useQueryClient();
  
  return useMutation<any, Error, { ids: number[]; reason?: string }>({
    mutationFn: async ({ ids, reason }) => {
      const response = await api.post('/pending-members/bulk-reject', { ids, reason });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pending-members'] });
      queryClient.invalidateQueries({ queryKey: ['pending-member-stats'] });
    },
  });
};

// Hook to delete a pending member
export const useDeletePendingMember = () => {
  const queryClient = useQueryClient();
  
  return useMutation<any, Error, number>({
    mutationFn: async (id) => {
      const response = await api.delete(`/pending-members/${id}`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pending-members'] });
      queryClient.invalidateQueries({ queryKey: ['pending-member-stats'] });
    },
  });
}; 